import React, { useState } from 'react';
import mockSynonymsData from '../../data/mockSynonyms.json';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CategoriesManagement from './CategoriesManagement';

/**
 * TagsSynonymsDetail Component
 * Comprehensive tags and synonyms management section
 * Allows viewing, adding, and managing tags and their synonyms
 *
 * @param {object} props
 * @param {array} props.tags - Current tags for the parameter
 * @param {array} props.synonyms - Current synonyms for the parameter
 * @param {array} props.availableTags - All available tags in the system
 * @param {function} props.onAddTag - Callback when tag is added
 * @param {function} props.onApproveTag - Callback when pending tag is approved
 * @param {function} props.onRemoveTag - Callback when tag is removed
 * @param {function} props.onAddSynonym - Callback when synonym is added
 * @param {function} props.onRemoveSynonym - Callback when synonym is removed
 * @param {string} props.currentUser - Current user name
 * @param {string} props.userRole - User role (user, data_steward, admin)
 */
const TagsSynonymsDetail = ({
  tags = [],
  synonyms = [],
  availableTags = [],
  onAddTag,
  onApproveTag,
  onRemoveTag,
  onAddSynonym,
  onRemoveSynonym,
  currentUser = 'Max Mustermann',
  userRole = 'data_steward'
}) => {
  const [newSynonym, setNewSynonym] = useState('');
  const [showAddSynonymDialog, setShowAddSynonymDialog] = useState(false);
  const [synonymLanguage, setSynonymLanguage] = useState('de');
  const [synonymContext, setSynonymContext] = useState('');
  const [commentText, setCommentText] = useState({});
  const [showCommentInput, setShowCommentInput] = useState({});

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [synonymToDelete, setSynonymToDelete] = useState(null);

  // Edit state for own synonyms
  const [editingSynonymId, setEditingSynonymId] = useState(null);
  const [editSynonymData, setEditSynonymData] = useState({ term: '', language: 'de', context: '' });

  // Mock data for demonstration - loaded from centralized JSON
  const [localSynonyms, setLocalSynonyms] = useState(
    mockSynonymsData.confirmedSynonyms
  );

  const [pendingSynonymSuggestions, setPendingSynonymSuggestions] = useState(
    mockSynonymsData.pendingSynonymSuggestions
  );

  // Old hard-coded data removed - now using mockSynonymsData from JSON file

  const handleAddSynonym = () => {
    if (newSynonym.trim()) {
      const newSyn = {
        id: Date.now(),
        term: newSynonym.trim(),
        language: synonymLanguage,
        context: synonymContext.trim(),
        addedBy: currentUser,
        addedDate: new Date().toISOString().split('T')[0],
        approved: false,
        approvedBy: null,
        likes: 0,
        dislikes: 0,
        likedBy: [],
        dislikedBy: [],
        comments: []
      };
      setLocalSynonyms([...localSynonyms, newSyn]);
      setNewSynonym('');
      setSynonymContext('');
      setShowAddSynonymDialog(false);

      if (onAddSynonym) {
        onAddSynonym(newSyn);
      }
    }
  };

  const handleRemoveSynonym = (synonymId) => {
    setLocalSynonyms(localSynonyms.filter(s => s.id !== synonymId));
    if (onRemoveSynonym) {
      onRemoveSynonym(synonymId);
    }
  };

  const handleApproveSynonym = (suggestionId) => {
    const suggestion = pendingSynonymSuggestions.find(s => s.id === suggestionId);
    if (suggestion) {
      const approved = {
        id: suggestion.id,
        term: suggestion.term,
        language: suggestion.language,
        context: suggestion.context,
        addedBy: suggestion.suggestedBy,
        addedDate: new Date().toISOString().split('T')[0],
        approved: true,
        approvedBy: currentUser
      };
      setLocalSynonyms([...localSynonyms, approved]);
      setPendingSynonymSuggestions(pendingSynonymSuggestions.filter(s => s.id !== suggestionId));
    }
  };

  const handleRejectSuggestion = (suggestionId) => {
    setPendingSynonymSuggestions(pendingSynonymSuggestions.filter(s => s.id !== suggestionId));
  };

  // Delete handlers for confirmed synonyms
  const handleDeleteConfirmedSynonymClick = (synonym) => {
    setSynonymToDelete(synonym);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (synonymToDelete) {
      setLocalSynonyms(localSynonyms.filter(s => s.id !== synonymToDelete.id));
      if (onRemoveSynonym) {
        onRemoveSynonym(synonymToDelete.id);
      }
    }
    setDeleteDialogOpen(false);
    setSynonymToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSynonymToDelete(null);
  };

  // Handlers for confirmed synonyms
  const handleLikeConfirmedSynonym = (synonymId) => {
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && s.approved) {
          const wasDisliked = s.dislikedBy?.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : (s.dislikedBy || []);
          const newDislikes = wasDisliked ? (s.dislikes || 0) - 1 : (s.dislikes || 0);

          return {
            ...s,
            likes: (s.likes || 0) + 1,
            likedBy: [...(s.likedBy || []), currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeConfirmedSynonym = (synonymId) => {
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && s.approved) {
          const wasLiked = s.likedBy?.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : (s.likedBy || []);
          const newLikes = wasLiked ? (s.likes || 0) - 1 : (s.likes || 0);

          return {
            ...s,
            dislikes: (s.dislikes || 0) + 1,
            dislikedBy: [...(s.dislikedBy || []), currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddCommentToConfirmedSynonym = (synonymId) => {
    const comment = commentText[`confirmed_${synonymId}`];
    if (comment && comment.trim()) {
      setLocalSynonyms(
        localSynonyms.map(s =>
          s.id === synonymId && s.approved
            ? {
                ...s,
                comments: [
                  ...(s.comments || []),
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [`confirmed_${synonymId}`]: '' });
      setShowCommentInput({ ...showCommentInput, [`confirmed_${synonymId}`]: false });
    }
  };

  // Handlers for pending synonyms (from others)
  const handleLikeSynonym = (suggestionId) => {
    setPendingSynonymSuggestions(
      pendingSynonymSuggestions.map(s => {
        if (s.id === suggestionId) {
          // Remove from disliked if previously disliked
          const wasDisliked = s.dislikedBy.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : s.dislikedBy;
          const newDislikes = wasDisliked ? s.dislikes - 1 : s.dislikes;

          // Add to liked
          return {
            ...s,
            likes: s.likes + 1,
            likedBy: [...s.likedBy, currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeSynonym = (suggestionId) => {
    setPendingSynonymSuggestions(
      pendingSynonymSuggestions.map(s => {
        if (s.id === suggestionId) {
          // Remove from liked if previously liked
          const wasLiked = s.likedBy.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : s.likedBy;
          const newLikes = wasLiked ? s.likes - 1 : s.likes;

          // Add to disliked
          return {
            ...s,
            dislikes: s.dislikes + 1,
            dislikedBy: [...s.dislikedBy, currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddComment = (suggestionId) => {
    const comment = commentText[suggestionId];
    if (comment && comment.trim()) {
      setPendingSynonymSuggestions(
        pendingSynonymSuggestions.map(s =>
          s.id === suggestionId
            ? {
                ...s,
                comments: [
                  ...s.comments,
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [suggestionId]: '' });
      setShowCommentInput({ ...showCommentInput, [suggestionId]: false });
    }
  };

  // Handlers for own unapproved synonyms
  const handleLikeOwnSynonym = (synonymId) => {
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && !s.approved) {
          const wasDisliked = s.dislikedBy?.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : (s.dislikedBy || []);
          const newDislikes = wasDisliked ? (s.dislikes || 0) - 1 : (s.dislikes || 0);

          return {
            ...s,
            likes: (s.likes || 0) + 1,
            likedBy: [...(s.likedBy || []), currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeOwnSynonym = (synonymId) => {
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && !s.approved) {
          const wasLiked = s.likedBy?.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : (s.likedBy || []);
          const newLikes = wasLiked ? (s.likes || 0) - 1 : (s.likes || 0);

          return {
            ...s,
            dislikes: (s.dislikes || 0) + 1,
            dislikedBy: [...(s.dislikedBy || []), currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddCommentToOwnSynonym = (synonymId) => {
    const comment = commentText[`own_${synonymId}`];
    if (comment && comment.trim()) {
      setLocalSynonyms(
        localSynonyms.map(s =>
          s.id === synonymId && !s.approved
            ? {
                ...s,
                comments: [
                  ...(s.comments || []),
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [`own_${synonymId}`]: '' });
      setShowCommentInput({ ...showCommentInput, [`own_${synonymId}`]: false });
    }
  };

  // Edit handlers for own synonyms
  const handleEditOwnSynonym = (synonym) => {
    setEditingSynonymId(synonym.id);
    setEditSynonymData({
      term: synonym.term,
      language: synonym.language,
      context: synonym.context || ''
    });
  };

  const handleSaveEditSynonym = () => {
    if (editSynonymData.term.trim()) {
      setLocalSynonyms(
        localSynonyms.map(s =>
          s.id === editingSynonymId
            ? {
                ...s,
                term: editSynonymData.term.trim(),
                language: editSynonymData.language,
                context: editSynonymData.context.trim()
              }
            : s
        )
      );
      setEditingSynonymId(null);
      setEditSynonymData({ term: '', language: 'de', context: '' });
    }
  };

  const handleCancelEditSynonym = () => {
    setEditingSynonymId(null);
    setEditSynonymData({ term: '', language: 'de', context: '' });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left: Categories (Tags) Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Kategorien
            </Typography>
            <CategoriesManagement
              tags={tags}
              availableTags={availableTags}
              onAddTag={onAddTag}
              onApproveTag={onApproveTag}
              onRemoveTag={onRemoveTag}
              currentUser={currentUser}
              userRole={userRole}
            />
          </Paper>
        </Grid>

        {/* Right: Synonyms Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                Synonyme
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
              Alternative Bezeichnungen für diesen Parameter (z.B. Abkürzungen, andere Sprachen)
            </Typography>

            {/* Approved Synonyms - with scrolling */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Bestätigte Synonyme
              </Typography>
              <Box
                sx={{
                  maxHeight: '350px',
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#555',
                    }
                  }
                }}
              >
                <List dense disablePadding>
                  {localSynonyms
                    .filter(s => s.approved)
                    .map(synonym => (
                      <ListItem key={synonym.id} disablePadding sx={{ mb: 1 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, width: '100%', backgroundColor: '#e8f5e9', borderColor: '#66bb6a' }}>
                          {/* Header row with term, language, and like/dislike */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {synonym.term}
                              </Typography>
                              <Chip
                                label={synonym.language.toUpperCase()}
                                size="small"
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                              <CheckIcon color="success" fontSize="small" />
                            </Box>

                            {/* Like/Dislike/Comment/Delete buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleLikeConfirmedSynonym(synonym.id)}
                                  disabled={synonym.likedBy?.includes(currentUser)}
                                  aria-label={`Synonym "${synonym.term}" liken`}
                                  title="Synonym liken"
                                  sx={{
                                    p: 0.3,
                                    color: synonym.likedBy?.includes(currentUser) ? '#4caf50' : 'inherit'
                                  }}
                                >
                                  <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                                <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                  {synonym.likes || 0}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDislikeConfirmedSynonym(synonym.id)}
                                  disabled={synonym.dislikedBy?.includes(currentUser)}
                                  aria-label={`Synonym "${synonym.term}" disliken`}
                                  title="Synonym disliken"
                                  sx={{
                                    p: 0.3,
                                    color: synonym.dislikedBy?.includes(currentUser) ? '#f44336' : 'inherit'
                                  }}
                                >
                                  <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                                <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                  {synonym.dislikes || 0}
                                </Typography>
                              </Box>

                              {/* Comment button */}
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setShowCommentInput({
                                    ...showCommentInput,
                                    [`confirmed_${synonym.id}`]: !showCommentInput[`confirmed_${synonym.id}`]
                                  })
                                }
                                aria-label={`Kommentar zu "${synonym.term}" hinzufügen`}
                                title="Kommentar hinzufügen"
                                sx={{ p: 0.3 }}
                              >
                                <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                              </IconButton>

                              {/* Delete button - only for Data Steward/Admin */}
                              {(userRole === 'data_steward' || userRole === 'admin') && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteConfirmedSynonymClick(synonym)}
                                  aria-label={`Synonym "${synonym.term}" löschen`}
                                  title="Synonym löschen"
                                  sx={{
                                    p: 0.3,
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {/* Context and metadata */}
                          {synonym.context && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {synonym.context}
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary" display="block">
                            Hinzugefügt von {synonym.addedBy} am {new Date(synonym.addedDate).toLocaleDateString('de-DE')}
                          </Typography>
                          {synonym.approvedBy && (
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: synonym.comments?.length > 0 || showCommentInput[`confirmed_${synonym.id}`] ? 1 : 0 }}>
                              Genehmigt von {synonym.approvedBy}
                            </Typography>
                          )}

                          {/* Comment input (collapsible) */}
                          {showCommentInput[`confirmed_${synonym.id}`] && (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                placeholder="Kommentar hinzufügen..."
                                value={commentText[`confirmed_${synonym.id}`] || ''}
                                onChange={e =>
                                  setCommentText({ ...commentText, [`confirmed_${synonym.id}`]: e.target.value })
                                }
                                sx={{ mb: 0.5 }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleAddCommentToConfirmedSynonym(synonym.id)}
                                disabled={!commentText[`confirmed_${synonym.id}`]?.trim()}
                                sx={{ py: 0.3 }}
                              >
                                Kommentar senden
                              </Button>
                            </Box>
                          )}

                          {/* Display existing comments */}
                          {synonym.comments && synonym.comments.length > 0 && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #c8e6c9' }}>
                              {synonym.comments.map((comment, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <Typography variant="caption" fontWeight="bold">
                                      {comment.author}:
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                      {new Date(comment.date).toLocaleDateString('de-DE')}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption">
                                    {comment.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Paper>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Add Synonym Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => setShowAddSynonymDialog(true)}
            >
              Synonym vorschlagen
            </Button>

            {/* Pending Synonym Suggestions - Moved inside Synonyms section */}
            {(pendingSynonymSuggestions.length > 0 || localSynonyms.filter(s => !s.approved).length > 0) && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <InfoOutlinedIcon color="warning" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Ausstehende Vorschläge
                  </Typography>
                  <Chip
                    label={pendingSynonymSuggestions.length + localSynonyms.filter(s => !s.approved).length}
                    size="small"
                    color="warning"
                  />
                </Box>

                <Typography variant="caption" color="textSecondary" gutterBottom display="block" sx={{ mb: 2 }}>
                  Diese Synonyme warten noch auf Bestätigung durch Data Stewards oder die Community
                </Typography>

                {/* Pending Suggestions from others */}
                {pendingSynonymSuggestions.map(suggestion => (
                  <Paper key={suggestion.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff9e6', borderColor: '#ff9800', mb: 1.5 }}>
                    {/* Header with term and action buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {suggestion.term}
                        </Typography>
                        <Chip
                          label={suggestion.language.toUpperCase()}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      {/* Like/Dislike/Comment buttons on the right */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeSynonym(suggestion.id)}
                            disabled={suggestion.likedBy.includes(currentUser)}
                            aria-label={`Synonym-Vorschlag "${suggestion.term}" liken`}
                            title="Vorschlag liken"
                            sx={{
                              p: 0.3,
                              color: suggestion.likedBy.includes(currentUser) ? '#4caf50' : 'inherit'
                            }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {suggestion.likes}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeSynonym(suggestion.id)}
                            disabled={suggestion.dislikedBy.includes(currentUser)}
                            aria-label={`Synonym-Vorschlag "${suggestion.term}" disliken`}
                            title="Vorschlag disliken"
                            sx={{
                              p: 0.3,
                              color: suggestion.dislikedBy.includes(currentUser) ? '#f44336' : 'inherit'
                            }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {suggestion.dislikes}
                          </Typography>
                        </Box>

                        {/* Comment button */}
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowCommentInput({
                              ...showCommentInput,
                              [suggestion.id]: !showCommentInput[suggestion.id]
                            })
                          }
                          aria-label={`Kommentar zu Vorschlag "${suggestion.term}" hinzufügen`}
                          title="Kommentar hinzufügen"
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Context and suggested by */}
                    {suggestion.context && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Kontext: {suggestion.context}
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: suggestion.comments?.length > 0 || showCommentInput[suggestion.id] ? 1 : 0 }}>
                      Vorgeschlagen von {suggestion.suggestedBy}
                    </Typography>

                    {/* Action buttons - only for Data Stewards/Admins */}
                    {(userRole === 'data_steward' || userRole === 'admin') && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => handleApproveSynonym(suggestion.id)}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          Genehmigen
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => handleRejectSuggestion(suggestion.id)}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          Ablehnen
                        </Button>
                      </Box>
                    )}

                    {/* Comment input (collapsible) */}
                    {showCommentInput[suggestion.id] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Kommentar hinzufügen..."
                          value={commentText[suggestion.id] || ''}
                          onChange={e =>
                            setCommentText({ ...commentText, [suggestion.id]: e.target.value })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddComment(suggestion.id)}
                          disabled={!commentText[suggestion.id]?.trim()}
                          sx={{ py: 0.3 }}
                        >
                          Kommentar senden
                        </Button>
                      </Box>
                    )}

                    {/* Display existing comments */}
                    {suggestion.comments && suggestion.comments.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                        {suggestion.comments.map((comment, idx) => (
                          <Box key={idx} sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                              <Typography variant="caption" fontWeight="bold">
                                {comment.author}:
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                {new Date(comment.date).toLocaleDateString('de-DE')}
                              </Typography>
                            </Box>
                            <Typography variant="caption">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}

                {/* User's own unapproved synonyms */}
                {localSynonyms
                  .filter(s => !s.approved)
                  .map(synonym => {
                    const isOwnSynonym = synonym.addedBy === currentUser;
                    return (
                      <Paper key={synonym.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff9e6', borderColor: '#ff9800', mb: 1.5 }}>
                        {editingSynonymId === synonym.id ? (
                          /* Edit Mode */
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              Synonym bearbeiten
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              label="Synonym"
                              value={editSynonymData.term}
                              onChange={(e) => setEditSynonymData({ ...editSynonymData, term: e.target.value })}
                              sx={{ mb: 1.5 }}
                            />
                            <Autocomplete
                              options={['de', 'en', 'fr', 'es']}
                              value={editSynonymData.language}
                              onChange={(e, value) => setEditSynonymData({ ...editSynonymData, language: value || 'de' })}
                              renderInput={(params) => (
                                <TextField {...params} label="Sprache" size="small" />
                              )}
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label="Kontext (optional)"
                              value={editSynonymData.context}
                              onChange={(e) => setEditSynonymData({ ...editSynonymData, context: e.target.value })}
                              multiline
                              rows={2}
                              sx={{ mb: 1.5 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveEditSynonym}
                                disabled={!editSynonymData.term.trim()}
                                sx={{ flex: 1 }}
                              >
                                Speichern
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCancelEditSynonym}
                                sx={{ flex: 1 }}
                              >
                                Abbrechen
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          /* Normal View */
                          <>
                            {/* Header with term and action buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {synonym.term}
                                </Typography>
                                <Chip
                                  label={synonym.language.toUpperCase()}
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              </Box>

                              {/* Like/Dislike/Comment/Edit/Delete buttons */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleLikeOwnSynonym(synonym.id)}
                                    disabled={isOwnSynonym || synonym.likedBy?.includes(currentUser)}
                                    aria-label={`Eigenen Vorschlag "${synonym.term}" liken`}
                                    title="Eigenen Vorschlag liken"
                                    sx={{
                                      p: 0.3,
                                      color: synonym.likedBy?.includes(currentUser) ? '#4caf50' : 'inherit'
                                    }}
                                  >
                                    <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                  <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                    {synonym.likes || 0}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDislikeOwnSynonym(synonym.id)}
                                    disabled={isOwnSynonym || synonym.dislikedBy?.includes(currentUser)}
                                    aria-label={`Eigenen Vorschlag "${synonym.term}" disliken`}
                                    title="Eigenen Vorschlag disliken"
                                    sx={{
                                      p: 0.3,
                                      color: synonym.dislikedBy?.includes(currentUser) ? '#f44336' : 'inherit'
                                    }}
                                  >
                                    <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                  <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                    {synonym.dislikes || 0}
                                  </Typography>
                                </Box>

                                {/* Comment button */}
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setShowCommentInput({
                                      ...showCommentInput,
                                      [`own_${synonym.id}`]: !showCommentInput[`own_${synonym.id}`]
                                    })
                                  }
                                  aria-label={`Kommentar zu eigenem Vorschlag "${synonym.term}" hinzufügen`}
                                  title="Kommentar hinzufügen"
                                  sx={{ p: 0.3 }}
                                >
                                  <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>

                                {/* Edit button - only for the creator */}
                                {isOwnSynonym && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditOwnSynonym(synonym)}
                                    aria-label={`Eigenen Vorschlag "${synonym.term}" bearbeiten`}
                                    title="Eigenen Vorschlag bearbeiten"
                                    sx={{ p: 0.3 }}
                                  >
                                    <EditIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                )}

                                {/* Delete button - only for the creator */}
                                {isOwnSynonym && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveSynonym(synonym.id)}
                                    aria-label={`Eigenen Vorschlag "${synonym.term}" löschen`}
                                    title="Eigenen Vorschlag löschen"
                                    sx={{
                                      p: 0.3,
                                      color: 'error.main',
                                      '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>

                            {/* Context and suggested by */}
                            {synonym.context && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Kontext: {synonym.context}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: synonym.comments?.length > 0 || showCommentInput[`own_${synonym.id}`] ? 1 : 0 }}>
                              Vorgeschlagen von {synonym.addedBy}
                            </Typography>
                          </>
                        )}

                        {/* Comment input (collapsible) */}
                        {showCommentInput[`own_${synonym.id}`] && (
                          <Box sx={{ mt: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Kommentar hinzufügen..."
                              value={commentText[`own_${synonym.id}`] || ''}
                              onChange={e =>
                                setCommentText({ ...commentText, [`own_${synonym.id}`]: e.target.value })
                              }
                              sx={{ mb: 0.5 }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleAddCommentToOwnSynonym(synonym.id)}
                              disabled={!commentText[`own_${synonym.id}`]?.trim()}
                              sx={{ py: 0.3 }}
                            >
                              Kommentar senden
                            </Button>
                          </Box>
                        )}

                        {/* Display existing comments */}
                        {synonym.comments && synonym.comments.length > 0 && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            {synonym.comments.map((comment, idx) => (
                              <Box key={idx} sx={{ mb: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {comment.author}:
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                    {new Date(comment.date).toLocaleDateString('de-DE')}
                                  </Typography>
                                </Box>
                                <Typography variant="caption">
                                  {comment.text}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Synonym Dialog */}
      <Dialog open={showAddSynonymDialog} onClose={() => setShowAddSynonymDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Synonym vorschlagen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Synonym"
              placeholder="Alternative Bezeichnung eingeben..."
              value={newSynonym}
              onChange={(e) => setNewSynonym(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            <Autocomplete
              options={['de', 'en', 'fr', 'es']}
              value={synonymLanguage}
              onChange={(e, value) => setSynonymLanguage(value || 'de')}
              renderInput={(params) => (
                <TextField {...params} label="Sprache" />
              )}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Kontext (optional)"
              placeholder="z.B. Klinische Abkürzung, Umgangssprachlich..."
              value={synonymContext}
              onChange={(e) => setSynonymContext(e.target.value)}
              multiline
              rows={2}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                Ihr Vorschlag wird zur Prüfung an das Data Steward Team gesendet. Bei ausreichender Community-Unterstützung kann das Synonym automatisch genehmigt werden.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddSynonymDialog(false)}>Abbrechen</Button>
          <Button
            onClick={handleAddSynonym}
            variant="contained"
            disabled={!newSynonym.trim()}
          >
            Vorschlagen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Synonym löschen?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Möchten Sie dieses bestätigte Synonym wirklich löschen?
            </Typography>
          </Alert>
          {synonymToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {synonymToDelete.term}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Sprache: {synonymToDelete.language.toUpperCase()}
              </Typography>
              {synonymToDelete.context && (
                <Typography variant="caption" display="block" color="textSecondary">
                  Kontext: {synonymToDelete.context}
                </Typography>
              )}
              <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                Hinzugefügt von: {synonymToDelete.addedBy} • {synonymToDelete.addedDate}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagsSynonymsDetail;
